'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { NavArrows } from '@ironhack/design-system2/components/nav-arrows';
import {
  Stack,
  type StackProps,
} from '@ironhack/design-system2/components/stack';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import slugify from '@sindresorhus/slugify';
import Script from 'next/script';
import { complement, isNil, path, propSatisfies } from 'ramda';
import { type ComponentType, type ReactElement, useState } from 'react';
import { type ItemList, type ListItem, type WithContext } from 'schema-dts';
import { type SetOptional } from 'type-fest';

import { type ArticleCardData } from '@/app/components/client/article-card';
import { Card } from '@/app/components/client/card';
import { getHref } from '@/app/components/client/link';
import { CancelWrap } from '@/app/components/server/cancel-wrap';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import {
  type DatoCardBlock,
  type DatoUserCardBlock,
} from '@/lib/datocms/types';

export type CardCarouselCard =
  | ArticleCardData
  | DatoCardBlock
  | DatoUserCardBlock;

export type CardCarouselCardList<
  TCard extends CardCarouselCard = CardCarouselCard
> = {
  __typename?: string;
  cards: TCard[];
  visibleCards: string;
};

export type CardCarouselCardComponent<
  TCard extends CardCarouselCard = CardCarouselCard
> = ComponentType<{ card: TCard } & FlexProps>;

const isDatoCardsArray = (array: unknown[]): array is DatoCardBlock[] =>
  array.every((card): card is DatoCardBlock =>
    propSatisfies(complement(isNil), 'title', card as DatoCardBlock)
  );

const DESKTOP_WIDTH = 1120;
const CARD_SEPARATION = 16;

const SCROLL_BREAKPOINT = 'lg';

type GenericProps<TCard extends CardCarouselCard> = {
  cardComponent: CardCarouselCardComponent<TCard>;
  cards: TCard[];
  smallScreenCardDirection?: 'horizontal' | 'vertical';
  visibleCards: number;
} & StackProps;

// Can only be called with type matching between component and array of cards
type Props =
  | SetOptional<GenericProps<DatoCardBlock>, 'cardComponent'>
  | GenericProps<DatoUserCardBlock>
  | GenericProps<ArticleCardData>;

export const CardCarousel = (props: Props): ReactElement => {
  const {
    cardComponent: CardComponent = Card,
    cards,
    smallScreenCardDirection = 'horizontal',
    visibleCards,
    ...stackProps
  } = props;

  const isHorizontalMobile = smallScreenCardDirection === 'horizontal';
  const scrollBreakpoint = isHorizontalMobile ? SCROLL_BREAKPOINT : 'md';

  const {
    params: { language, region },
  } = usePageContext();

  const isScrollMode = useBreakpointValue({
    base: true,
    [scrollBreakpoint]: false,
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const hasNext = currentIndex < cards.length - visibleCards;
  const handleNext = () =>
    setCurrentIndex((prevIndex) =>
      prevIndex === cards.length - 1 ? prevIndex : prevIndex + 1
    );

  const hasPrev = currentIndex > 0;
  const handlePrev = () =>
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? 0 : prevIndex - 1));

  const scrollUnit =
    (DESKTOP_WIDTH - CARD_SEPARATION * (visibleCards - 1)) / visibleCards +
    CARD_SEPARATION;

  const seoStructuredData: WithContext<ItemList> | null = isDatoCardsArray(
    cards
  )
    ? // Consider using <script> if this ItemList is impactful to SEO
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: cards.map((card, index): ListItem => {
          const target = path(['button', '0', 'target', '0'], card);
          const url =
            target && getHref(target, { language, region, withOrigin: true });
          const hasTarget = Boolean(url);
          const name = card.title || card.subtitle || '';
          return {
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@id': hasTarget ? String(url) : slugify(name),
              name,
              ...(hasTarget && { url }),
              ...(card.media4XL?.responsiveImage && {
                image: card.media4XL.responsiveImage.src,
              }),
            },
          };
        }),
      }
    : null;

  return (
    <>
      {cards.length > 0 && (
        <Box>
          <CancelWrap as={Flex} below={scrollBreakpoint} withPadding>
            <Stack
              {...stackProps}
              alignItems="flex-start"
              as="ul"
              direction="row"
              gap={2}
              spacing={0}
              transform={[
                null,
                null,
                `translateX(-${currentIndex * scrollUnit}px)`,
              ]}
              transition="transform 0.5s"
              {...(!isHorizontalMobile && {
                justify: 'center',
                minW: ['100%', null, 'auto'],
                wrap: 'wrap',
              })}
            >
              {cards.map((cardToDisplay, index) => (
                <CardComponent
                  as="li"
                  // @ts-expect-error -- Ok if components accept different cards
                  card={cardToDisplay}
                  key={cardToDisplay.id ?? index}
                  {...(isHorizontalMobile
                    ? { h: 'full' }
                    : {
                        maxW: '100%',
                        minW: ['100%', 'auto'],
                        swapMobileMedia: true,
                      })}
                />
              ))}
            </Stack>
          </CancelWrap>
          {cards.length > visibleCards && !isScrollMode && (
            <NavArrows
              hasNext={hasNext}
              hasPrev={hasPrev}
              justifyContent="flex-end"
              mt={3}
              onNext={handleNext}
              onPrev={handlePrev}
              w="full"
            />
          )}
          {seoStructuredData && (
            <Script
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(seoStructuredData),
              }}
              id="structured-data-carousel"
              strategy="lazyOnload"
              type="application/ld+json"
            />
          )}
        </Box>
      )}
    </>
  );
};
